import { supabase } from '../../config/supabase';
import { getSentQuotationDepartmentsForJobs } from './quotations';
import { computeEstimationTotals } from './finalPricingModes';

export const CALENDAR_DEPARTMENTS = [
    { key: 'roof', label: 'Roof' },
    { key: 'wall', label: 'Wall' },
    { key: 'floor', label: 'Floor' },
];

const SUPPORTED_DEPARTMENTS = CALENDAR_DEPARTMENTS.map(department => department.key);

const DEPARTMENT_LABELS = CALENDAR_DEPARTMENTS.reduce((result, department) => {
    result[department.key] = department.label;
    return result;
}, {});

const PRICING_SELECT = `
    id,
    company_id,
    job_id,
    department,
    final_price,
    notes,
    updated_at
`;

// job_quotes has no direct FK to job_final_pricings, so job_final_pricings
// is embedded through jobs (jobs -> job_final_pricings is a real FK, same
// embedding final-pricings.js and quotations.js already rely on) rather
// than attempted as a sibling embed off job_quotes directly.
//
// job_pricing_entries is always selected in full since every company can have
// fallback-priced entries now (after dropping the mode-based gating).
const buildEntrySelect = () => `
    id,
    job_id,
    quote_type,
    expected_delivery_date,
    expected_finish_date,
    is_required,
    jobs!inner (
        id,
        company_id,
        unique_id,
        customer_name,
        customer_address,
        job_type,
        is_cancelled,
        deleted,
        job_final_pricings(${PRICING_SELECT}),
        job_pricing_entries(*)
    )
`;

const normalizeEntry = quote => {
    const job = quote.jobs;
    const pricingRecords = Array.isArray(job.job_final_pricings) ? job.job_final_pricings : [];
    const pricing = pricingRecords.find(record => record.department === quote.quote_type) || null;
    const dateValue = quote.expected_delivery_date || quote.expected_finish_date;

    return {
        // A job can legitimately carry more than one job_quotes row for the
        // same department, so entryId must key off the quote row itself
        // (quote.id is already unique) rather than job+department, or React
        // sees duplicate list keys and the two rows get merged as one.
        entryId: quote.id,
        quoteId: quote.id,
        department: quote.quote_type,
        departmentLabel: DEPARTMENT_LABELS[quote.quote_type] || quote.quote_type,
        date: dateValue ? new Date(dateValue) : null,
        pricing,
        pricingEntries: Array.isArray(job.job_pricing_entries) ? job.job_pricing_entries : [],
        sent: false,
        job: {
            id: job.id,
            unique_id: job.unique_id,
            customer_name: job.customer_name,
            address: job.customer_address,
            job_type: job.job_type,
            is_cancelled: !!job.is_cancelled,
        },
    };
};

/**
 * Fetch every (job, department) entry whose delivery date (falling back to
 * finish date, same fallback getMonthlyInvoiceAssignments in
 * monthlySchedule.js already uses) falls inside [rangeStart, rangeEnd).
 *
 * Only department/job-type/cancelled/date-range filters run here at the DB
 * level. Pricing status, price range, quotation-sent status, and free-text
 * search are intentionally NOT applied in this function — the page
 * component derives a filtered view client-side from this raw set, so
 * toggling those filters (or a realtime patch arriving) never triggers a
 * network refetch.
 */
export const getFinalPricingCalendarEntries = async (companyId, {
    rangeStart,
    rangeEnd,
    departments = SUPPORTED_DEPARTMENTS,
    jobType = 'all',
    cancelledStatus = 'active',
} = {}) => {
    if (!companyId || !rangeStart || !rangeEnd) {
        return { data: [] };
    }

    const activeDepartments = departments.filter(key => SUPPORTED_DEPARTMENTS.includes(key));
    if (activeDepartments.length === 0) {
        return { data: [] };
    }

    const buildQuery = () => {
        let query = supabase
            .from('job_quotes')
            .select(buildEntrySelect())
            .eq('jobs.company_id', companyId)
            .eq('jobs.deleted', false)
            .eq('is_required', true)
            .in('quote_type', activeDepartments)
            // Playground-pinned quotes have no real schedule date, but expected_finish_date
            // is often left set, so they'd otherwise slip in through the finish-date fallback
            // below. eq(false) alone would drop legitimate NULL rows (NULL = true is NULL,
            // not false), so both false and NULL are accepted explicitly for each column.
            .or(
                'and(quote_playground.is.null,delivery_playground.is.null),' +
                'and(quote_playground.is.null,delivery_playground.eq.false),' +
                'and(quote_playground.eq.false,delivery_playground.is.null),' +
                'and(quote_playground.eq.false,delivery_playground.eq.false)'
            )
            .or(
                `and(expected_delivery_date.gte.${rangeStart},expected_delivery_date.lt.${rangeEnd}),and(expected_delivery_date.is.null,expected_finish_date.gte.${rangeStart},expected_finish_date.lt.${rangeEnd})`
            )
            .order('expected_delivery_date', { ascending: true, nullsFirst: false })
            .order('expected_finish_date', { ascending: true, nullsFirst: false })
            // Both date columns have many ties, and .range() pagination below
            // needs a fully deterministic order or the same row can land on
            // two pages (and a different row gets skipped entirely). id is
            // unique, so it closes that gap.
            .order('id', { ascending: true });

        if (jobType && jobType !== 'all') {
            query = query.eq('jobs.job_type', jobType);
        }

        if (cancelledStatus === 'active') {
            query = query.eq('jobs.is_cancelled', false);
        } else if (cancelledStatus === 'cancelled') {
            query = query.eq('jobs.is_cancelled', true);
        }

        return query;
    };

    // Supabase/PostgREST caps a single request at its configured max-rows
    // (1000 by default) and silently truncates anything past that instead of
    // erroring — a company with a busy year can have several thousand rows
    // matching this query. Page through with .range() until a page comes
    // back short of PAGE_SIZE, so Year view (and busy Month views) get every
    // matching row instead of just the first page.
    const PAGE_SIZE = 1000;
    const MAX_PAGES = 50; // 50,000 rows — comfortably past any realistic result set
    let data = [];
    for (let page = 0; page < MAX_PAGES; page++) {
        const from = page * PAGE_SIZE;
        const to = from + PAGE_SIZE - 1;
        const { data: pageData, error } = await buildQuery().range(from, to);
        if (error) throw error;
        data = data.concat(pageData || []);
        if (!pageData || pageData.length < PAGE_SIZE) break;
    }

    const entries = (data || [])
        .map(normalizeEntry)
        .filter(entry => !!entry.date);

    const jobIds = [...new Set(entries.map(entry => entry.job.id))];
    if (jobIds.length === 0) {
        return { data: entries };
    }

    // job_quotes returns one top-level row per (job, department) quote, so a
    // 3-department job embeds 3 textually-identical but referentially
    // distinct job_pricing_entries arrays — PostgREST evaluates the nested
    // embed per parent row and doesn't dedupe/share JSON substructures
    // across sibling rows for the same job, and JSON.parse never
    // structurally shares equal-content arrays either. Computing
    // computeEstimationTotals per entry would therefore re-sum the same
    // job's records once per department. Instead, compute it exactly once
    // per unique job id here and share the single result object across
    // every department-entry for that job (see the `estimationTotals`
    // field attached below).
    const estimationTotalsByJob = new Map();
    entries.forEach(entry => {
        if (!estimationTotalsByJob.has(entry.job.id)) {
            estimationTotalsByJob.set(entry.job.id, computeEstimationTotals(entry.pricingEntries));
        }
    });

    const sentMap = await getSentQuotationDepartmentsForJobs(companyId, jobIds);

    return {
        data: entries.map(entry => ({
            ...entry,
            sent: !!sentMap[entry.job.id]?.has(entry.department),
            estimationTotals: estimationTotalsByJob.get(entry.job.id) || null,
        })),
    };
};

/**
 * job_quotes has no company_id column of its own (it only references jobs
 * via job_id), so unlike the other three channels here, that subscription
 * can't filter by company at the Postgres level — same limitation
 * MonthlyInvoiceView works around. The page component defends against this
 * by only patching entries it already has in local state and ignoring
 * anything else, so cross-company noise on that one channel is harmless.
 */
export const subscribeToFinalPricingCalendar = (companyId, {
    onPricingChange,
    onQuoteChange,
    onJobChange,
    onConversationChange,
} = {}) => {
    if (!companyId) return [];

    return [
        supabase
            .channel(`fpc-pricings-${companyId}`)
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'job_final_pricings', filter: `company_id=eq.${companyId}` },
                payload => onPricingChange?.(payload)
            )
            .subscribe(),

        supabase
            .channel(`fpc-quotes-${companyId}`)
            .on(
                'postgres_changes',
                { event: 'UPDATE', schema: 'public', table: 'job_quotes' },
                payload => onQuoteChange?.(payload)
            )
            .subscribe(),

        supabase
            .channel(`fpc-jobs-${companyId}`)
            .on(
                'postgres_changes',
                { event: 'UPDATE', schema: 'public', table: 'jobs', filter: `company_id=eq.${companyId}` },
                payload => onJobChange?.(payload)
            )
            .subscribe(),

        supabase
            .channel(`fpc-conversations-${companyId}`)
            .on(
                'postgres_changes',
                { event: 'INSERT', schema: 'public', table: 'customer_conversations', filter: `company_id=eq.${companyId}` },
                payload => onConversationChange?.(payload)
            )
            .subscribe(),
    ];
};

export const unsubscribeFromFinalPricingCalendar = async channels => {
    if (!Array.isArray(channels)) return;

    await Promise.all(
        channels.map(channel => (channel ? supabase.removeChannel(channel) : Promise.resolve()))
    );
};
